/**
 * Quote history for Chip-seek.
 *
 * - RFQ links across catalog rows are recorded in localStorage when clicked.
 * - The Quote History page renders those records with re-submit and email
 *   actions. Records are browser-local; official quotes come by email.
 */
(function () {
  const STORAGE_KEY = "ics_quote_requests";
  const MAX_RECORDS = 100;

  function readRecords() {
    try {
      const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      return [];
    }
  }

  function writeRecords(records) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(records));
    } catch (error) {
      // Storage unavailable; keep session behavior.
    }
  }

  function recordQuote(link) {
    const records = readRecords();
    const part = link.dataset.quotePart || "";
    const manufacturer = link.dataset.quoteManufacturer || "";
    const url = link.dataset.quoteUrl || "";
    const record = {
      part: part,
      manufacturer: manufacturer,
      url: url,
      quantity: "",
      note: "",
      sent: false,
      createdAt: new Date().toISOString(),
    };
    records.unshift(record);
    writeRecords(records.slice(0, MAX_RECORDS));
    publishCount();
  }

  function publishCount() {
    const count = readRecords().length;
    document.dispatchEvent(
      new CustomEvent("ics:quote-history:changed", { detail: { count: count } }),
    );
  }

  document.addEventListener("click", function (event) {
    const link = event.target.closest("[data-quote-record]");
    if (!link) return;
    recordQuote(link);
  });

  /* ------------------------------------------------------------------ */
  /* Quote History page renderer                                        */
  /* ------------------------------------------------------------------ */

  const list = document.querySelector("[data-quote-history-list]");
  if (!list) return;

  const empty = document.querySelector("[data-quote-history-empty]");
  const toolbar = document.querySelector("[data-quote-history-toolbar]");
  const clearButton = document.querySelector("[data-quote-history-clear]");
  const countElement = document.querySelector("[data-quote-count]");
  const form = document.querySelector("[data-quote-history-form]");
  const status = document.querySelector("[data-quote-history-status]");
  const rfqPath = (list.dataset.rfqPath || "/pages/bom-rfq").replace(/\/$/, "");

  function escapeHtml(value) {
    return String(value == null ? "" : value)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");
  }

  function formatDate(value) {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    return date.toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  }

  function rfqHref(record) {
    return (
      rfqPath +
      "?part=" +
      encodeURIComponent(record.part) +
      "&manufacturer=" +
      encodeURIComponent(record.manufacturer)
    );
  }

  function mailtoFor(record) {
    const email = list.dataset.email || "sales@chip-seek.com";
    const subject = "RFQ follow-up: " + (record.part || "part");
    const lines = [
      "RFQ follow-up from the Chip-seek quote history page.",
      "",
      "MPN: " + (record.part || ""),
      "Manufacturer: " + (record.manufacturer || ""),
      "Quantity: " + (record.quantity || "Not specified"),
    ];
    if (record.note) {
      lines.push("", "Additional detail:", record.note);
    }
    lines.push("", "Please confirm current availability, price and lead time.");
    return (
      "mailto:" +
      encodeURIComponent(email) +
      "?subject=" +
      encodeURIComponent(subject) +
      "&body=" +
      encodeURIComponent(lines.join("\n"))
    );
  }

  function render() {
    const records = readRecords();
    if (!records.length) {
      list.hidden = true;
      if (empty) empty.hidden = false;
      if (toolbar) toolbar.hidden = true;
      return;
    }
    list.hidden = false;
    if (empty) empty.hidden = true;
    if (toolbar) toolbar.hidden = false;
    list.innerHTML = records
      .map(function (record, index) {
        const submitted = record.sent ? "Submitted" : "Pending";
        return (
          '<li class="ics-history-item" data-history-item data-index="' + index + '">' +
          '<div class="ics-history-item__main">' +
          '<span class="ics-history-item__status ' +
          (record.sent ? "ics-history-item__status--sent" : "") +
          '">' + submitted + "</span>" +
          "<strong>" + escapeHtml(record.part || "Part") + "</strong>" +
          (record.manufacturer
            ? '<span class="ics-history-item__vendor">' + escapeHtml(record.manufacturer) + "</span>"
            : "") +
          '<span class="ics-history-item__meta">' +
          escapeHtml(formatDate(record.createdAt)) +
          (record.quantity ? " · Qty " + escapeHtml(record.quantity) : "") +
          "</span>" +
          (record.note ? '<small class="ics-history-item__note">' + escapeHtml(record.note) + "</small>" : "") +
          "</div>" +
          '<div class="ics-history-item__actions">' +
          '<a class="ics-history-item__action" href="' + escapeHtml(rfqHref(record)) + '">Re-submit RFQ</a>' +
          '<a class="ics-history-item__action" data-history-send="' + index + '" href="' + escapeHtml(mailtoFor(record)) + '">Send by email</a>' +
          '<button type="button" class="ics-history-item__remove" data-history-remove="' + index + '">Remove</button>' +
          "</div>" +
          "</li>"
        );
      })
      .join("");
    if (countElement) countElement.textContent = String(records.length);
  }

  function showStatus(message, isError) {
    if (!status) return;
    status.textContent = message;
    status.hidden = false;
    if (isError) {
      status.setAttribute("data-error", "true");
    } else {
      status.removeAttribute("data-error");
    }
  }

  if (form) {
    form.addEventListener("submit", function (event) {
      event.preventDefault();
      if (!form.reportValidity()) {
        showStatus("Please enter the part number before adding the record.", true);
        return;
      }
      const data = new FormData(form);
      const record = {
        part: String(data.get("part") || "").trim(),
        manufacturer: String(data.get("manufacturer") || "").trim(),
        quantity: String(data.get("quantity") || "").trim(),
        note: String(data.get("note") || "").trim(),
        url: "",
        sent: false,
        createdAt: new Date().toISOString(),
      };
      const records = readRecords();
      records.unshift(record);
      writeRecords(records.slice(0, MAX_RECORDS));
      form.reset();
      render();
      showStatus("Record added to this browser. Official quotes are confirmed by email.", false);
    });
  }

  list.addEventListener("click", function (event) {
    const remove = event.target.closest("[data-history-remove]");
    if (!remove) return;
    const index = Number(remove.dataset.historyRemove);
    const records = readRecords();
    if (Number.isFinite(index) && records[index]) {
      records.splice(index, 1);
      writeRecords(records);
    }
    render();
  });

  if (clearButton) {
    clearButton.addEventListener("click", function () {
      writeRecords([]);
      render();
    });
  }

  document.addEventListener("click", function (event) {
    const send = event.target.closest("[data-history-send]");
    if (!send) return;
    const index = Number(send.dataset.historySend);
    const records = readRecords();
    if (Number.isFinite(index) && records[index]) {
      records[index].sent = true;
      writeRecords(records);
      render();
    }
  });

  render();
})();
